import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Download, HelpCircle, BarChart3 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CURRENCIES, UPGRADE_DEFINITIONS } from "../lib/calculations";
import { useI18n } from "../lib/i18n";
import { CaseSafe } from "./CaseSafe";

// ===========================================================================
// Shared building blocks
// ===========================================================================

const SectionCard = ({ title, children, testId }) => (
  <section
    className="rounded-2xl border border-[#E2E8F0] bg-white p-6 md:p-8 space-y-5"
    data-testid={testId}
  >
    <h3 className="font-display text-lg font-medium tracking-tight text-[#0F172A]">
      {title}
    </h3>
    <div className="space-y-4">{children}</div>
  </section>
);

const InfoTip = ({ text, testId }) => {
  const { t } = useI18n();
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={0}
            aria-label={t("common.more_info")}
            data-testid={testId}
            className="inline-flex items-center justify-center text-[#94A3B8] hover:text-[#002D5B] focus:outline-none focus:text-[#002D5B] transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] bg-[#0F172A] text-white text-[11px] leading-relaxed py-2 px-3"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const FieldLabel = ({ children, tip, tipTestId, badge }) => (
  <Label className="text-xs font-semibold tracking-wider uppercase text-[#64748B] flex items-center justify-between">
    <span className="flex items-center gap-1.5">
      <span><CaseSafe text={children} /></span>
      {tip && <InfoTip text={tip} testId={tipTestId} />}
    </span>
    {badge && (
      <span className="text-[9px] font-medium tracking-[0.15em] text-[#94A3B8] uppercase">
        {badge}
      </span>
    )}
  </Label>
);

const NumberField = ({ label, value, onChange, step = 1, testId, suffix, min = 0, tip, tipTestId }) => {
  const [draft, setDraft] = React.useState(String(value ?? ""));
  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    if (!focusedRef.current) {
      setDraft(String(value ?? ""));
    }
  }, [value]);

  return (
    <div className="space-y-1.5">
      <FieldLabel tip={tip} tipTestId={tipTestId}>
        {label}
      </FieldLabel>
      <div className="relative">
        <Input
          type="number"
          value={draft}
          step={step}
          min={min}
          onFocus={(e) => {
            focusedRef.current = true;
            requestAnimationFrame(() => e.target.select());
          }}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw);
            if (raw !== "") onChange(Number(raw));
          }}
          onBlur={() => {
            focusedRef.current = false;
            if (draft === "" || draft === "-") {
              setDraft("0");
              onChange(0);
            } else if (Number(draft) < min) {
              setDraft(String(min));
              onChange(min);
            }
          }}
          data-testid={testId}
          className="font-body text-base h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#002D5B] focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[#94A3B8] tracking-wide pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, testId, type = "text", placeholder, badge }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold tracking-wider uppercase text-[#64748B] flex items-center justify-between">
      <span>{label}</span>
      {badge && (
        <span className="text-[9px] font-medium tracking-[0.15em] text-[#94A3B8] uppercase">
          {badge}
        </span>
      )}
    </Label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="font-body text-base h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#002D5B] focus-visible:ring-offset-0"
    />
  </div>
);

const clampPct = (raw) => (raw === "" ? 0 : Math.max(0, Math.min(100, Number(raw))));

const UpgradeRow = ({ definition, state, onToggle, onPctChange, onCoverageChange }) => {
  const { t } = useI18n();
  const enabled = !!state?.enabled;
  const pct = state?.savingsPct ?? definition.defaultPct;
  const coverage = state?.coveragePct ?? definition.defaultCoveragePct ?? 100;
  const label = t(`strategy.${definition.id}.label`);
  const tooltip = t(`strategy.${definition.id}.tooltip`);
  const coverageNote =
    definition.hasCoverage ? t(`strategy.${definition.id}.coverageNote`) : null;
  // Optional per-strategy override that replaces the generic
  // "<prefix> <label>" composition when a locale needs a fully natural phrase.
  const coverageLabelKey = `strategy.${definition.id}.coverageLabel`;
  const customCoverageLabel = definition.hasCoverage ? t(coverageLabelKey) : null;
  const coverageLabel =
    customCoverageLabel && customCoverageLabel !== coverageLabelKey
      ? customCoverageLabel
      : `${t("upgrades.coverage_label_prefix")} ${label}`;
  const note =
    definition.id === "spaceOptimisation"
      ? t(`strategy.${definition.id}.note`)
      : null;
  return (
    <div
      className={`rounded-lg border transition-colors ${
        enabled ? "border-[#002D5B]/30 bg-[#F8FAFC]" : "border-[#E2E8F0] bg-white"
      }`}
      data-testid={`toggle-row-${definition.id}`}
    >
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            data-testid={`toggle-${definition.id}`}
            className="data-[state=checked]:bg-[#002D5B]"
          />
          <span className="font-body text-sm font-semibold text-[#0F172A] truncate">
            {label}
          </span>
          <InfoTip text={tooltip} testId={`tip-upgrade-${definition.id}`} />
        </div>
        <div className="relative w-24 shrink-0">
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={pct}
            disabled={!enabled}
            onFocus={(e) => requestAnimationFrame(() => e.target.select())}
            onChange={(e) =>
              onPctChange(clampPct(e.target.value))
            }
            data-testid={`pct-${definition.id}`}
            className="h-9 pr-7 text-right font-display text-base tabular-nums border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#002D5B] focus-visible:ring-offset-0 disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-[#94A3B8] pointer-events-none">
            %
          </span>
        </div>
      </div>

      {definition.hasCoverage && (
        <div
          className={`mx-4 mb-3 -mt-1 rounded-md border border-dashed ${
            enabled ? "border-[#002D5B]/25 bg-white" : "border-[#E2E8F0] bg-white/60"
          } p-3 flex items-center justify-between gap-3`}
          data-testid={`coverage-block-${definition.id}`}
        >
          <div className="flex-1 min-w-0">
            <Label
              className={`text-[10px] font-semibold tracking-[0.15em] uppercase ${
                enabled ? "text-[#475569]" : "text-[#94A3B8]"
              }`}
            >
              <CaseSafe text={coverageLabel} />
            </Label>
            {coverageNote && (
              <p
                className={`text-[10.5px] leading-snug mt-1 ${
                  enabled ? "text-[#64748B]" : "text-[#94A3B8]"
                }`}
              >
                {coverageNote}
              </p>
            )}
          </div>
          <div className="relative w-24 shrink-0">
            <Input
              type="number"
              min={0}
              max={100}
              step={1}
              value={coverage}
              disabled={!enabled}
              onFocus={(e) => requestAnimationFrame(() => e.target.select())}
              onChange={(e) =>
                onCoverageChange(clampPct(e.target.value))
              }
              data-testid={`coverage-${definition.id}`}
              className="h-9 pr-7 text-right font-display text-base tabular-nums border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#002D5B] focus-visible:ring-offset-0 disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-[#94A3B8] pointer-events-none">
              %
            </span>
          </div>
        </div>
      )}

      {definition.insights && (
        <p
          className="px-4 pb-3 -mt-1 flex items-center gap-1.5 text-[11px] text-[#002D5B] leading-relaxed"
          data-testid={`insights-note-${definition.id}`}
        >
          <BarChart3 className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
          <span>{t("upgrades.insights_tip")}</span>
        </p>
      )}

      {note && (
        <p
          className="px-4 pb-3 -mt-1 text-[11px] italic text-[#64748B] leading-relaxed"
          data-testid={`note-${definition.id}`}
        >
          {note}
        </p>
      )}
    </div>
  );
};

const useStateHelpers = (state, setState) => {
  const update = (field) => (value) => setState((s) => ({ ...s, [field]: value }));
  const setUpgrade = (id, patch) =>
    setState((s) => ({
      ...s,
      upgrades: { ...s.upgrades, [id]: { ...s.upgrades[id], ...patch } },
    }));
  return { update, setUpgrade };
};

// ===========================================================================
// 01 — Project Details
// ===========================================================================

export const ProjectDetailsSection = ({ state, setState }) => {
  const { t } = useI18n();
  const { update } = useStateHelpers(state, setState);
  return (
    <SectionCard title={t("projectDetails.title")} testId="section-project-details">
      <TextField
        label={t("projectDetails.client_name")}
        value={state.clientName}
        onChange={update("clientName")}
        placeholder={t("projectDetails.client_placeholder")}
        testId="input-client-name"
      />
      <TextField
        label={t("projectDetails.project_name")}
        value={state.projectName}
        onChange={update("projectName")}
        placeholder={t("projectDetails.project_placeholder")}
        testId="input-project-name"
      />
      <TextField
        label={t("projectDetails.date")}
        type="date"
        value={state.projectDate}
        onChange={update("projectDate")}
        testId="input-project-date"
      />

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold tracking-wider uppercase text-[#64748B] flex items-center justify-between">
          <span>{t("projectDetails.contact_email")}</span>
          <span className="text-[9px] font-bold tracking-[0.18em] text-[#002D5B] bg-[#002D5B]/[0.08] px-1.5 py-0.5 rounded">
            {t("common.required_for_pdf")}
          </span>
        </Label>
        <Input
          type="email"
          value={state.contactEmail}
          onChange={(e) => update("contactEmail")(e.target.value)}
          placeholder={t("projectDetails.email_placeholder")}
          data-testid="input-contact-email"
          className="font-body text-base h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#002D5B] focus-visible:ring-offset-0"
        />
        <p className="text-[11px] text-[#64748B] leading-relaxed">
          {t("projectDetails.contact_help")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField
          label={t("projectDetails.company")}
          value={state.company}
          onChange={update("company")}
          placeholder={t("projectDetails.company_placeholder")}
          testId="input-company"
          badge={t("common.optional")}
        />
        <TextField
          label={t("projectDetails.job_title")}
          value={state.jobTitle}
          onChange={update("jobTitle")}
          placeholder={t("projectDetails.job_placeholder")}
          testId="input-job-title"
          badge={t("common.optional")}
        />
      </div>

      <div
        className="flex items-start gap-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3.5"
        data-testid="gdpr-consent-block"
      >
        <Checkbox
          id="gdpr-consent"
          checked={!!state.gdprConsent}
          onCheckedChange={(checked) => update("gdprConsent")(checked === true)}
          data-testid="checkbox-gdpr-consent"
          className="mt-0.5 border-[#94A3B8] data-[state=checked]:bg-[#002D5B] data-[state=checked]:border-[#002D5B] data-[state=checked]:text-white"
        />
        <Label
          htmlFor="gdpr-consent"
          className="text-[11px] leading-relaxed text-[#475569] font-normal cursor-pointer"
        >
          {t("projectDetails.gdpr_lead")}{" "}
          <a
            href="https://helvar.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-[#002D5B] hover:text-[#001F40]"
            onClick={(e) => e.stopPropagation()}
          >
            {t("projectDetails.gdpr_link")}
          </a>
          .
        </Label>
      </div>
    </SectionCard>
  );
};

// ===========================================================================
// 02 — Facility Metrics
// ===========================================================================

export const FacilityMetricsSection = ({
  state,
  setState,
  currencyCode,
  setCurrencyCode,
}) => {
  const { t } = useI18n();
  const { update } = useStateHelpers(state, setState);
  const symbol = CURRENCIES[currencyCode].symbol;
  const isNewBuild = state.projectType === "newBuild";
  return (
    <SectionCard title={t("facility.title")} testId="section-facility-metrics">
      <div className="space-y-1.5">
        <FieldLabel
          tip={t("facility.tip_project_type")}
          tipTestId="tip-project-type"
        >
          {t("facility.project_type")}
        </FieldLabel>
        <Select
          value={state.projectType || "renovation"}
          onValueChange={update("projectType")}
        >
          <SelectTrigger
            data-testid="select-project-type"
            className="h-11 border-[#E2E8F0] focus:ring-2 focus:ring-[#002D5B] focus:ring-offset-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="renovation" data-testid="project-type-option-renovation">
              {t("facility.type_renovation")}
            </SelectItem>
            <SelectItem value="newBuild" data-testid="project-type-option-newBuild">
              {t("facility.type_new_build")}
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-[#64748B] leading-relaxed">
          {isNewBuild ? t("facility.type_help_new") : t("facility.type_help_reno")}
        </p>
      </div>

      {!isNewBuild && (
        <div className="grid grid-cols-2 gap-3" data-testid="renovation-only-luminaires">
          <NumberField
            label={t("facility.existing_luminaires")}
            value={state.existingLuminaires}
            onChange={update("existingLuminaires")}
            step={1}
            testId="input-existing-luminaires"
            suffix={t("units.units")}
            tip={t("facility.tip_existing_luminaires")}
            tipTestId="tip-existing-luminaires"
          />
          <NumberField
            label={t("facility.new_luminaires")}
            value={state.luminaires}
            onChange={update("luminaires")}
            step={1}
            testId="input-luminaires"
            suffix={t("units.units")}
            tip={t("facility.tip_new_luminaires_reno")}
            tipTestId="tip-new-luminaires"
          />
        </div>
      )}
      {isNewBuild && (
        <NumberField
          label={t("facility.luminaires")}
          value={state.luminaires}
          onChange={update("luminaires")}
          step={1}
          testId="input-luminaires"
          suffix={t("units.units")}
          tip={t("facility.tip_new_luminaires_new")}
          tipTestId="tip-new-luminaires"
        />
      )}
      <NumberField
        label={t("facility.burning_hours")}
        value={state.burningHoursPerWeek}
        onChange={update("burningHoursPerWeek")}
        step={1}
        testId="input-burning-hours"
        suffix={t("units.hrs_wk")}
        tip={t("facility.tip_burning_hours")}
        tipTestId="tip-burning-hours"
      />

      {!isNewBuild && (
        <div className="grid grid-cols-2 gap-3" data-testid="renovation-only-wattage">
          <NumberField
            label={t("facility.existing_wattage")}
            value={state.existingWattage}
            onChange={update("existingWattage")}
            step={1}
            testId="input-existing-wattage"
            suffix="W"
            tip={t("facility.tip_existing_wattage")}
            tipTestId="tip-existing-wattage"
          />
          <NumberField
            label={t("facility.proposed_wattage")}
            value={state.proposedWattage}
            onChange={update("proposedWattage")}
            step={1}
            testId="input-proposed-wattage"
            suffix="W"
            tip={t("facility.tip_proposed_wattage")}
            tipTestId="tip-proposed-wattage"
          />
        </div>
      )}
      {isNewBuild && (
        <NumberField
          label={t("facility.proposed_wattage")}
          value={state.proposedWattage}
          onChange={update("proposedWattage")}
          step={1}
          testId="input-proposed-wattage"
          suffix="W"
          tip={t("facility.tip_proposed_wattage")}
          tipTestId="tip-proposed-wattage"
        />
      )}

      <div className="grid grid-cols-2 gap-3">
        {!isNewBuild && (
          <NumberField
            label={t("facility.existing_runtime")}
            value={state.existingRuntimePct}
            onChange={update("existingRuntimePct")}
            step={1}
            testId="input-existing-runtime"
            suffix="%"
            tip={t("facility.tip_existing_runtime")}
            tipTestId="tip-existing-runtime"
          />
        )}
        <NumberField
          label={t("facility.basic_controls_runtime")}
          value={state.upgradeRuntimePct}
          onChange={update("upgradeRuntimePct")}
          step={1}
          testId="input-upgrade-runtime"
          suffix="%"
          tip={t("facility.tip_basic_controls_runtime")}
          tipTestId="tip-basic-controls-runtime"
        />
      </div>
      <p className="text-[11px] text-[#64748B] leading-relaxed -mt-2">
        {t("facility.basic_controls_note")}
      </p>

      <NumberField
        label={t("facility.energy_price")}
        value={state.pricePerKwh}
        onChange={update("pricePerKwh")}
        step={0.01}
        testId="input-energy-price"
        suffix={`${symbol}/kWh`}
      />
      <NumberField
        label={t("facility.co2_factor")}
        value={state.co2PerKwh}
        onChange={update("co2PerKwh")}
        step={0.01}
        testId="input-co2-factor"
        suffix="kg/kWh"
        tip={t("facility.tip_co2_factor")}
        tipTestId="tip-co2-factor"
      />
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">
          {t("common.currency")}
        </Label>
        <Select value={currencyCode} onValueChange={setCurrencyCode}>
          <SelectTrigger
            data-testid="select-currency"
            className="h-11 border-[#E2E8F0] focus:ring-2 focus:ring-[#002D5B] focus:ring-offset-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(CURRENCIES).map((c) => (
              <SelectItem
                key={c.code}
                value={c.code}
                data-testid={`currency-option-${c.code}`}
              >
                {c.symbol} — {c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </SectionCard>
  );
};

// ===========================================================================
// 03 — Total Project CapEx Breakdown
// ===========================================================================

export const CapExSection = ({ state, setState, currencyCode }) => {
  const { t } = useI18n();
  const { update } = useStateHelpers(state, setState);
  const symbol = CURRENCIES[currencyCode].symbol;
  const compareOn = !!state.compareCompetitor;
  return (
    <SectionCard title={t("capex.title")} testId="section-capex">
      <NumberField
        label={t("capex.luminaire_cost")}
        value={state.luminaireCost}
        onChange={update("luminaireCost")}
        step={100}
        testId="input-luminaire-cost"
        suffix={symbol}
        tip={t("capex.tip_luminaire_cost")}
        tipTestId="tip-luminaire-cost"
      />
      <NumberField
        label={t("capex.installation")}
        value={state.installationCost}
        onChange={update("installationCost")}
        step={100}
        testId="input-installation-cost"
        suffix={symbol}
        tip={t("capex.tip_installation")}
        tipTestId="tip-installation-cost"
      />
      <NumberField
        label={t("capex.helvar_system")}
        value={state.helvarSystemCost}
        onChange={update("helvarSystemCost")}
        step={100}
        testId="input-helvar-system-cost"
        suffix={symbol}
        tip={t("capex.tip_helvar_system")}
        tipTestId="tip-helvar-cost"
      />

      <div
        className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 space-y-3"
        data-testid="competitor-compare-block"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-[#0F172A]">
                {t("capex.compare_competitor")}
              </span>
              <InfoTip
                text={t("capex.tip_compare_competitor")}
                testId="tip-compare-competitor"
              />
            </div>
            <p className="text-[11px] text-[#64748B] leading-relaxed mt-0.5">
              {compareOn ? t("capex.formula_on") : t("capex.formula_off")}
            </p>
          </div>
          <Switch
            checked={compareOn}
            onCheckedChange={(v) => update("compareCompetitor")(!!v)}
            data-testid="toggle-compare-competitor"
            className="data-[state=checked]:bg-[#002D5B] mt-0.5"
          />
        </div>

        {compareOn && (
          <NumberField
            label={t("capex.competitor_cost")}
            value={state.competitorControlsCost}
            onChange={update("competitorControlsCost")}
            step={100}
            testId="input-competitor-controls-cost"
            suffix={symbol}
            tip={t("capex.tip_competitor_cost")}
            tipTestId="tip-competitor-cost"
          />
        )}
      </div>
    </SectionCard>
  );
};

// ===========================================================================
// 04 — Helvar Control Upgrades
// ===========================================================================

export const HelvarUpgradesSection = ({ state, setState }) => {
  const { t } = useI18n();
  const { setUpgrade } = useStateHelpers(state, setState);
  return (
    <SectionCard title={t("upgrades.title")} testId="section-helvar-upgrades">
      <div
        className="rounded-lg border border-[#002D5B]/15 bg-[#002D5B]/[0.03] p-4 text-[11.5px] leading-relaxed text-[#475569] space-y-2"
        data-testid="en15193-description"
      >
        <p className="font-semibold text-[#0F172A]">{t("upgrades.en_title")}</p>
        <p>{t("upgrades.en_body_1")}</p>
        <p>{t("upgrades.en_body_2")}</p>
        <p className="text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-2.5 py-2">
          <span className="font-semibold">{t("upgrades.important_label")}</span>{" "}
          {t("upgrades.important_body")}
        </p>
      </div>
      <p className="text-xs text-[#64748B] leading-relaxed">
        {t("upgrades.strategies_note")}
      </p>
      {UPGRADE_DEFINITIONS.map((def) => (
        <UpgradeRow
          key={def.id}
          definition={def}
          state={state.upgrades[def.id]}
          onToggle={(enabled) => setUpgrade(def.id, { enabled })}
          onPctChange={(savingsPct) => setUpgrade(def.id, { savingsPct })}
          onCoverageChange={(coveragePct) => setUpgrade(def.id, { coveragePct })}
        />
      ))}
    </SectionCard>
  );
};

// ===========================================================================
// 05 — Executive Summary (printed onto PDF page 2)
// ===========================================================================

export const ExecutiveSummarySection = ({
  executiveSummary,
  setExecutiveSummary,
  onDownloadPdf,
  pdfLoading,
  isEmailValid,
  hasGdprConsent,
  canDownloadPdf,
  state,
}) => {
  const { t } = useI18n();
  const EXECUTIVE_MAX = 500;
  const summaryLen = executiveSummary ? executiveSummary.length : 0;
  const nearLimit = summaryLen >= EXECUTIVE_MAX - 40;
  const gateMessage = !isEmailValid
    ? (state?.contactEmail || "").trim().length === 0
      ? t("gate.email_missing")
      : t("gate.email_invalid")
    : !hasGdprConsent
    ? t("gate.consent_missing")
    : "";
  return (
    <SectionCard title={t("executive.title")} testId="section-executive-summary">
      <div className="space-y-1.5">
        <Textarea
          value={executiveSummary}
          onChange={(e) => setExecutiveSummary(e.target.value.slice(0, EXECUTIVE_MAX))}
          maxLength={EXECUTIVE_MAX}
          placeholder={t("executive.placeholder")}
          className="min-h-[140px] font-body text-sm text-[#0F172A] border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#002D5B] focus-visible:ring-offset-0"
          data-testid="textarea-executive-summary"
        />
        <div
          className={`text-[11px] font-medium tabular-nums text-right ${
            nearLimit ? "text-[#EA580C]" : "text-[#94A3B8]"
          }`}
          data-testid="executive-summary-counter"
        >
          {summaryLen} / {EXECUTIVE_MAX}
        </div>
      </div>

      {onDownloadPdf && (
        <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 no-print">
          {!canDownloadPdf && (
            <p
              className="text-[11px] text-[#EA580C] font-medium flex items-center gap-1.5 max-w-[360px]"
              data-testid="email-gate-hint-bottom"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0" />
              <span>{gateMessage}</span>
            </p>
          )}
          <Button
            onClick={onDownloadPdf}
            disabled={pdfLoading || !canDownloadPdf}
            className="h-11 bg-[#002D5B] hover:bg-[#001F40] text-white font-medium px-6 disabled:bg-[#94A3B8] disabled:opacity-100 disabled:cursor-not-allowed"
            data-testid="btn-download-pdf-bottom"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={2} />
            {pdfLoading ? t("common.generating") : t("common.download_pdf")}
          </Button>
        </div>
      )}
    </SectionCard>
  );
};
